import React from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { PatientFormBlock } from './PatientFormBlock';
import {
  PatientFormMediaContext,
  type PatientFormMediaContextValue,
} from './PatientFormMediaContext';
import type {
  PatientFormErrors,
  PatientFormLookupFn,
  PatientFormValues,
  ResolvedFormBlock,
} from '../../forms/patientFill/types';

export interface PatientFormViewProps {
  formName: string;
  intro?: string;
  blocks: ResolvedFormBlock[];
  values: PatientFormValues;
  errors?: PatientFormErrors;
  onChange: (blockId: string, value: unknown) => void;
  onSubmit: () => void;
  submitting?: boolean;
  submitLabel?: string;
  footerNote?: string;
  readOnly?: boolean;
  className?: string;
  onLookupSearch?: PatientFormLookupFn;
  /** Resolve stored upload keys (`forms/signatures/...`) to authenticated file URLs. */
  mediaContext?: PatientFormMediaContextValue;
  /** Render blocks only — no page/card chrome (use inside Modal or Drawer). */
  embedded?: boolean;
}

export function PatientFormView({
  formName,
  intro,
  blocks,
  values,
  errors,
  onChange,
  onSubmit,
  submitting = false,
  submitLabel = 'Submit form',
  footerNote,
  readOnly = false,
  className,
  onLookupSearch,
  mediaContext,
  embedded = false,
}: PatientFormViewProps) {
  const visibleBlocks = blocks.filter((b) => b.is_visible);
  const errorCount = errors ? Object.keys(errors).length : 0;

  const blockList = (
    <div className={cn('flex flex-col gap-[22px]', embedded ? 'px-6 py-5' : 'px-5 py-6 sm:px-8')}>
      {visibleBlocks.map((block) => {
        const err = errors?.[block.id];
        return (
          // Anchor so W17's client validation can scroll to the first offender.
          <div key={block.id} id={`pf-block-${block.id}`}>
            <PatientFormBlock
              block={block}
              value={values[block.id]}
              onChange={(v) => {
                onChange(block.id, v);
              }}
              disabled={readOnly}
              {...(onLookupSearch ? { onLookupSearch } : {})}
              {...(err ? { error: err } : {})}
            />
          </div>
        );
      })}
    </div>
  );

  const body = (
    <PatientFormMediaContext.Provider value={mediaContext ?? {}}>
      {intro && embedded ? (
        <p className="border-b border-[var(--cream-2)] px-6 py-4 text-[14px] leading-relaxed text-[var(--ink-2)]">
          {intro}
        </p>
      ) : null}
      {blockList}
    </PatientFormMediaContext.Provider>
  );

  if (embedded) {
    return (
      <div className={cn('w-full', className)}>
        {body}
      </div>
    );
  }

  return (
    <div className={cn('mx-auto w-full max-w-[720px] px-4 py-8 sm:px-6 sm:py-10', className)}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!readOnly && !submitting) onSubmit();
        }}
        className="overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-2)]"
      >
        <header className="border-b border-[var(--cream-2)] bg-gradient-to-b from-[var(--cream-0)] to-[var(--cream-1)] px-5 py-6 sm:px-8 sm:py-7">
          <h1 className="font-[var(--font-display)] text-[24px] font-semibold leading-tight tracking-[-0.03em] text-[var(--ink-0)] sm:text-[26px]">
            {formName}
          </h1>
          {intro ? <p className="mt-2.5 text-[14px] leading-relaxed text-[var(--ink-2)]">{intro}</p> : null}
        </header>

        {body}

        {errorCount > 0 ? (
          <div className="mx-5 mb-2 flex items-center gap-2 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--terracotta)_30%,var(--cream-2))] bg-[color-mix(in_srgb,var(--terracotta)_8%,var(--cream-1))] px-3.5 py-2.5 text-[12.5px] text-[var(--terracotta)] sm:mx-8">
            <Icon kind="alert" size={14} className="shrink-0" />
            Please fix {errorCount} field{errorCount === 1 ? '' : 's'} before submitting.
          </div>
        ) : null}

        {!readOnly ? (
          <div className="flex flex-wrap items-center justify-between gap-3.5 border-t border-[var(--cream-2)] px-5 py-5 sm:px-8 sm:py-6">
            <p className="text-[11.5px] leading-snug text-[var(--ink-4)]">
              {footerNote ?? 'Your information is encrypted and synced to your patient record.'}
            </p>
            <Button type="submit" size="lg" loading={submitting} icon="arrow-r">
              {submitLabel}
            </Button>
          </div>
        ) : null}
      </form>
    </div>
  );
}
